import {
  InfiniteData,
  QueryClient,
  useMutation,
  useQueryClient,
} from 'react-query';
import InAppNotificationsApi from 'app/api/inAppNotifications';
import { queryKey } from 'constants/queryKey';
import {
  InAppNotification,
  InAppNotificationsSummary,
  NotificationCount,
} from 'types/InAppNotification';

type NotificationPages = InfiniteData<InAppNotification[]>;

function markReadInLists(
  queryClient: QueryClient,
  isAffected: (notification: InAppNotification) => boolean,
) {
  queryClient.setQueriesData<NotificationPages | undefined>(
    queryKey.inAppNotifications(),
    (data) =>
      data && {
        ...data,
        pages: data.pages.map((page) =>
          page.map((notification) =>
            !notification.is_read && isAffected(notification)
              ? { ...notification, is_read: true }
              : notification,
          ),
        ),
      },
  );
}

function updateSummary(
  queryClient: QueryClient,
  update: (summary: InAppNotificationsSummary) => InAppNotificationsSummary,
) {
  queryClient.setQueryData<InAppNotificationsSummary | undefined>(
    queryKey.inAppNotificationsSummary(),
    (summary) => summary && update(summary),
  );
}

async function cancelAndSnapshot(queryClient: QueryClient) {
  await Promise.all([
    queryClient.cancelQueries(queryKey.inAppNotifications()),
    queryClient.cancelQueries(queryKey.inAppNotificationsSummary()),
  ]);

  return {
    lists: queryClient.getQueriesData<NotificationPages>(
      queryKey.inAppNotifications(),
    ),
    summary: queryClient.getQueryData<InAppNotificationsSummary>(
      queryKey.inAppNotificationsSummary(),
    ),
  };
}

type Snapshot = Awaited<ReturnType<typeof cancelAndSnapshot>>;

function restoreSnapshot(queryClient: QueryClient, snapshot?: Snapshot) {
  if (!snapshot) {
    return;
  }

  snapshot.lists.forEach(([key, data]) => queryClient.setQueryData(key, data));
  queryClient.setQueryData(
    queryKey.inAppNotificationsSummary(),
    snapshot.summary,
  );
}

function invalidateNotifications(queryClient: QueryClient) {
  queryClient.invalidateQueries(queryKey.inAppNotifications());
  queryClient.invalidateQueries(queryKey.inAppNotificationsSummary());
  queryClient.invalidateQueries(queryKey.inAppNotificationsCount());
}

export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation(InAppNotificationsApi.markAllAsRead, {
    onMutate: async () => {
      const snapshot = await cancelAndSnapshot(queryClient);

      markReadInLists(queryClient, () => true);
      updateSummary(queryClient, (summary) => ({
        collaboration: { unread: 0 },
        alerts: { ...summary.alerts, unread: 0 },
      }));

      return snapshot;
    },
    onError: (_error, _variables, snapshot) =>
      restoreSnapshot(queryClient, snapshot),
    onSettled: () => invalidateNotifications(queryClient),
  });
};

export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (notification: InAppNotification) =>
      InAppNotificationsApi.markAsRead(notification.id),
    {
      onMutate: async (notification) => {
        const snapshot = await cancelAndSnapshot(queryClient);

        markReadInLists(queryClient, ({ id }) => id === notification.id);
        updateSummary(queryClient, (summary) => ({
          ...summary,
          [notification.category]: {
            ...summary[notification.category],
            unread: Math.max(0, summary[notification.category].unread - 1),
          },
        }));

        return snapshot;
      },
      onError: (_error, _variables, snapshot) =>
        restoreSnapshot(queryClient, snapshot),
      onSettled: () => invalidateNotifications(queryClient),
    },
  );
};

export const useMarkAllNotificationsAsSeen = () => {
  const queryClient = useQueryClient();

  return useMutation(InAppNotificationsApi.markAllAsSeen, {
    onMutate: async () => {
      await queryClient.cancelQueries(queryKey.inAppNotificationsCount());

      const count = queryClient.getQueryData<NotificationCount>(
        queryKey.inAppNotificationsCount(),
      );

      queryClient.setQueryData<NotificationCount | undefined>(
        queryKey.inAppNotificationsCount(),
        (previous) => previous && { count: 0 },
      );

      return count;
    },
    onError: (_error, _variables, count) => {
      if (count) {
        queryClient.setQueryData(queryKey.inAppNotificationsCount(), count);
      }
    },
    onSettled: () =>
      queryClient.invalidateQueries(queryKey.inAppNotificationsCount()),
  });
};
